import * as React from 'react';
import { observer } from 'mobx-react';
import { Employee } from 'nomst/Stores/Decision/employee';
import { FormGroup, Select, Input } from '@payscale/design';
import { AmountCode } from 'typings/enums';
import { AutoComplete } from 'components/Common/autoComplete';
import Amount from 'nomst/Components/Decision/amount';
import { defaultSearchResultLimit, requestNewTitleCode, requestNewTitleText } from 'common/constants';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';

interface CreatePromotionRequestViewProps {
    employee: Employee;
    isCycleLocked: boolean;
    configuration: Configuration;
    countryDecisionDispatcher: ICountryDecisionDispatcher;
    disableInput: boolean;
}

interface CreatePromotionRequestViewState {
    isRequestSent: boolean;
    errorText: string;
}

export class CreatePromotionRequestView extends
    React.Component<CreatePromotionRequestViewProps, CreatePromotionRequestViewState> {
    requestNewTitleJob: IJobSummary = {
        code: requestNewTitleCode,
        jobCodeKey: '',
        title: requestNewTitleText,
        familyCode: '',
        ordinal: 0,
        minRange: 0,
        maxRange: 0,
        currency: ''
    } as IJobSummary;

    constructor(props: CreatePromotionRequestViewProps) {
        super(props);
        this.handleChangeJob = this.handleChangeJob.bind(this);
        this.handleTypeInJob = this.handleTypeInJob.bind(this);
        this.autocompleteSearchJobs = this.autocompleteSearchJobs.bind(this);
        this.setPromotionSubmittedFalse = this.setPromotionSubmittedFalse.bind(this);
        this.handleSetFoundJob = this.handleSetFoundJob.bind(this);

        this.state = Object.assign({}, this.state, {
            isRequestSent: false,
            errorText: ''
        });
    }

    render(): React.ReactNode {
        const { employee } = this.props;
        return (
            <div className="promotion-section">
                <div className="current-position">
                    <label htmlFor="current-job-title" className="current-title">Current Title</label>
                    <div id="current-job-title" className="current-job-title">{employee.jobTitleInternal}</div>
                </div>
                {this.renderJobPromotionForm()}
                {this.renderProposedJobTitle()}
                {this.renderPromotionAmount()}
            </div>
        );
    }

    private renderJobPromotionForm(): React.ReactNode {
        if (this.props.isCycleLocked) {
            return this.renderNonEditableJobPromotionForm();
        }

        return (
            <div className="promote-to">
                <label htmlFor='selectPromotionJob' className="promote-to-text required">Promote this employee to</label>
                <FormGroup error={this.state.errorText} className="promote-to-form-group">
                    {this.renderEditableJobPromotion()}
                </FormGroup>
            </div>
        );
    }

    private renderNonEditableJobPromotionForm(): React.ReactNode {
        const { employee } = this.props;

        let promotionTitle = 'No promotion requested';
        if (employee.hasPromotionRequest) {
            const jobSummary = employee.promotionRequest.promotionJobList.find(
                job => job.code === employee.promotionRequest.proposedJobCode);
            if (jobSummary) {
                promotionTitle = jobSummary.title;
            }
        }

        return (
            <div className="readonly-job-promotion">
                <p className="readonly-job-promotion-text">Promote this employee to</p>
                <p className="readonly-job-promotion-title">{promotionTitle}</p>
            </div>
        );
    }

    private renderPromotionAmount(): React.ReactNode {
        const { configuration, countryDecisionDispatcher, employee, isCycleLocked } = this.props;
        const cycleBudgetField = configuration.getCycleBudgetField(AmountCode.Promotion);
        return (
            <div className="promotion-amount">
                <Amount
                    configuration={configuration}
                    employeeSourceId={employee.employeeSourceId}
                    amount={employee.promotionRequest.promotionAmount}
                    countryDecisionDispatcher={countryDecisionDispatcher}
                    title={cycleBudgetField ? cycleBudgetField.title : ''}
                    isHourly={employee.isHourly}
                    annualStandardWorkHours={employee.annualStandardWorkHours}
                    fullTimeEquivalent={employee.fullTimeEquivalent}
                    isReadOnly={isCycleLocked}
                    idSuffix="promotion"
                />
            </div>
        );
    }

    private renderEditableJobPromotion(): React.ReactNode {
        const { employee } = this.props;
        const { promotionRequest } = employee;

        if (promotionRequest.promotionJobList && promotionRequest.promotionJobList.length < defaultSearchResultLimit) {
            return this.renderDropdownJobPromotion();
        }

        return this.renderAutoCompleteJobPromotion();
    }

    private renderProposedJobTitle(): React.ReactNode {
        const { disableInput } = this.props;
        const { promotionRequest } = this.props.employee;
        if (promotionRequest.proposedJobCode !== requestNewTitleCode) {
            return null;
        }

        return (
            <div className="proposed-job-title">
                <label htmlFor="newJobTitleInput" className="proposed-job-title__label">
                    Proposed Job Title
                </label>
                <FormGroup error={this.jobInvalidText}>
                    <Input
                        value={promotionRequest.proposedJobTitle}
                        onChange={this.handleTypeInJob}
                        disabled={disableInput}
                        id="newJobTitleInput"
                    />
                </FormGroup>
            </div>
        );
    }

    private renderDropdownJobPromotion(): React.ReactNode {
        const { employee } = this.props;
        const { promotionRequest } = employee;

        if (!promotionRequest) {
            return null;
        }

        const jobOptions = [{
            text: 'Select a Job Title',
            value: ''
        }];

        jobOptions.push(...promotionRequest.sortedPromotionJobsList.map((job) => {
            return {
                text: job.title,
                value: job.code
            };
        }));

        jobOptions.push({
            text: requestNewTitleText,
            value: requestNewTitleCode
        });

        return (
            <div className="promote-to-dropdown" >
                <Select
                    id="selectPromotionJob"
                    value={promotionRequest.proposedJobCode}
                    onChange={this.handleChangeJob}
                    options={jobOptions}
                />
            </div>
        );
    }

    private renderAutoCompleteJobPromotion(): React.ReactNode {
        const initialValue = this.getAutocompleteInitialValue();
        return (
            <div className="promote-to-autocomplete" >
                <AutoComplete
                    id="selectPromotionJob"
                    placeholder={'Search for a Job Title'}
                    search={this.autocompleteSearchJobs}
                    resultLimit={defaultSearchResultLimit}
                    onChangeInput={this.setPromotionSubmittedFalse}
                    onSuggestionSelected={this.handleSetFoundJob}
                    onClearSearch={this.setPromotionSubmittedFalse}
                    getSelectedValue={this.getJobTitle}
                    getRenderedSuggestionText={this.getJobTitle}
                    initialValue={initialValue}
                    disableInput={this.props.isCycleLocked}
                />
            </div>
        );
    }

    private handleSetFoundJob(value: IJobNoMst): void {
        const { promotionRequest } = this.props.employee;
        promotionRequest.handleSetFoundJob(value);
    }

    private getAutocompleteInitialValue(): string {
        const { employee } = this.props;
        const { promotionRequest } = employee;

        if (promotionRequest.proposedJobCode === requestNewTitleCode) {
            return requestNewTitleText;
        }

        const selectedJob = employee.promotionRequest.promotionJobList.find(
            job => job && job.code === promotionRequest.proposedJobCode);
        return selectedJob ? selectedJob.title : '';
    }

    private get jobInvalidText(): string {
        if (this.isJobValid) {
            return '';
        }

        return '* Required Field';
    }

    private get isJobValid(): boolean {
        const { promotionRequest } = this.props.employee;
        return (!this.isRequestNewTitleCode(promotionRequest.proposedJobCode) &&
            this.isValidString(promotionRequest.proposedJobCode)) ||
            (this.isRequestNewTitleCode(promotionRequest.proposedJobCode) &&
            this.isValidString(promotionRequest.proposedJobTitle));
    }

    private getJobTitle(job: IJobSummary): string {
        return job.title;
    }

    private autocompleteSearchJobs(searchText: string, resultLimit: number): Promise<IJobSummary[]> {
        const { promotionJobList } = this.props.employee.promotionRequest;

        if (!searchText || searchText.trim().length === 0) {
            return Promise.resolve(promotionJobList.slice(0, resultLimit));
        }

        const result = promotionJobList.filter(item => {
            return item.title.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
        }).slice(0, resultLimit);

        result.push(this.requestNewTitleJob);
        return Promise.resolve(result);
    }

    private setPromotionSubmittedFalse(): void {
        this.setState({ isRequestSent: false });
    }

    private isRequestNewTitleCode(value: string): boolean {
        return !!value && value === requestNewTitleCode;
    }

    private isValidString(value: string): boolean {
        return !!value && value.trim().length > 0;
    }

    private handleTypeInJob(_: undefined, value: string): void {
        const { promotionRequest } = this.props.employee;
        promotionRequest.handleSetTypedJob(value);
        this.setState({ errorText: this.jobInvalidText });
    }

    private handleChangeJob(_: undefined, value: string): void {
        const { promotionRequest } = this.props.employee;
        promotionRequest.handleChangeJob(value);
        this.setState({ errorText: this.jobInvalidText });
    }
}

export default observer(CreatePromotionRequestView);